/**
 * Encrypts auth-related values (tokens, user, permissions) before they touch
 * localStorage, using a non-extractable AES-GCM key kept in IndexedDB (never
 * exported as raw bytes, never written alongside the ciphertext). This protects
 * against someone reading the localStorage file directly off disk (a stolen
 * device, a shared machine, a browser-profile backup) — it does NOT protect
 * against live XSS in this tab, since a script running in-page can call the
 * same encrypt/decrypt functions this module exposes. There is no browser
 * storage API immune to that; the real fix for XSS-token-theft is httpOnly
 * cookies set by the backend, which is outside this frontend's control.
 *
 * Because Web Crypto is async-only and most of the app (Axios-style fetch
 * headers, Pinia store init) needs synchronous reads, decrypted values are
 * mirrored into an in-memory cache. Call `hydrateSecureStorage()` once at boot
 * (before routing/store init) to populate it; after that, get/set/remove are
 * synchronous, with encryption happening in the background on write.
 */

const DB_NAME = 'kantiro-secure-storage'
const KEY_STORE = 'keys'
const CRYPTO_KEY_ID = 'primary'
const STORAGE_PREFIX = 'sec:'

const memoryCache = new Map<string, string>()
let cryptoKeyPromise: Promise<CryptoKey> | null = null

function cryptoAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && typeof crypto !== 'undefined' && !!crypto.subtle
}

function openKeyDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(KEY_STORE)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getOrCreateCryptoKey(): Promise<CryptoKey> {
  if (!cryptoKeyPromise) {
    cryptoKeyPromise = (async () => {
      const db = await openKeyDb()

      const existing = await new Promise<CryptoKey | undefined>((resolve, reject) => {
        const tx = db.transaction(KEY_STORE, 'readonly')
        const req = tx.objectStore(KEY_STORE).get(CRYPTO_KEY_ID)
        req.onsuccess = () => resolve(req.result as CryptoKey | undefined)
        req.onerror = () => reject(req.error)
      })
      if (existing) return existing

      const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ])
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(KEY_STORE, 'readwrite')
        tx.objectStore(KEY_STORE).put(key, CRYPTO_KEY_ID)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
      })
      return key
    })()
  }
  return cryptoKeyPromise
}

function toBase64(bytes: Uint8Array): string {
  let binary = ''
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)))
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

async function encrypt(value: string): Promise<string> {
  const key = await getOrCreateCryptoKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(value)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return `${toBase64(iv)}.${toBase64(new Uint8Array(ciphertext))}`
}

async function decrypt(payload: string): Promise<string | null> {
  try {
    const [ivPart, dataPart] = payload.split('.')
    if (!ivPart || !dataPart) return null
    const key = await getOrCreateCryptoKey()
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromBase64(ivPart) },
      key,
      fromBase64(dataPart),
    )
    return new TextDecoder().decode(plain)
  } catch (error) {
    console.error('Failed to decrypt secure storage value:', error)
    return null
  }
}

/**
 * Loads every given key into the in-memory cache, decrypting as needed. Also
 * migrates any legacy plaintext value (written before this module existed)
 * into encrypted storage so existing logged-in users aren't signed out.
 * Must be awaited once at app boot, before anything reads these keys.
 */
export async function hydrateSecureStorage(keys: string[]): Promise<void> {
  const canEncrypt = cryptoAvailable()

  await Promise.all(
    keys.map(async (key) => {
      const encrypted = localStorage.getItem(STORAGE_PREFIX + key)
      if (encrypted) {
        if (!canEncrypt) return
        const plain = await decrypt(encrypted)
        if (plain !== null) memoryCache.set(key, plain)
        return
      }

      // Legacy plaintext value from before encryption — migrate it in place.
      const legacy = localStorage.getItem(key)
      if (legacy === null) return
      memoryCache.set(key, legacy)
      localStorage.removeItem(key)

      if (!canEncrypt) {
        localStorage.setItem(STORAGE_PREFIX + key, legacy)
        return
      }
      try {
        localStorage.setItem(STORAGE_PREFIX + key, await encrypt(legacy))
      } catch (error) {
        console.error('Failed to migrate legacy value to secure storage:', error)
      }
    }),
  )
}

export function getSecureItem(key: string): string | null {
  return memoryCache.get(key) ?? null
}

export function setSecureItem(key: string, value: string): void {
  memoryCache.set(key, value)

  if (!cryptoAvailable()) {
    localStorage.setItem(STORAGE_PREFIX + key, value)
    return
  }
  void encrypt(value)
    .then((ciphertext) => localStorage.setItem(STORAGE_PREFIX + key, ciphertext))
    .catch((error) => console.error('Failed to persist secure storage value:', error))
}

export function removeSecureItem(key: string): void {
  memoryCache.delete(key)
  localStorage.removeItem(STORAGE_PREFIX + key)
  localStorage.removeItem(key) // clean up any pre-migration leftover
}

export const SECURE_AUTH_KEYS = [
  'accessToken',
  'refreshToken',
  'user',
  'organization',
  'permissions',
  'orgPermissions',
  'roles',
  'client',
  'userEmail',
]
